import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { StandingsSidebar, DesktopLayout, LiveAnswer, LoadingQuestion, WaitingSpinner } from '../shared';
import { GameEndScreen } from './GameEndScreen';
import type { GameStatistics } from '../../types/game';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import '../../App.css';
import '../../styles/game-layout.css';
import { useTranslation } from '../../i18n';

interface SpeedChallengeQuestion {
  questionId?: string;
  id?: string;
  question: string;
  options: string[];
  timeLimit: number;
  questionNumber: number;
  totalQuestions: number;
  startTime: number;
  interesting?: string;
  challengeType: 'type-word' | 'emoji-identify' | 'math' | 'reverse' | 'color-sequence' | 'pattern' | 'shape';
  displayContent: string;
  displayColors?: string[];
  isSpeedChallenge?: boolean;
}

interface QuestionResult {
  questionId: string;
  correctAnswer: string;
  submissions: Array<{
    playerId: string;
    answer: string;
    responseTime: number;
    isCorrect: boolean;
    points: number;
  }>;
  leaderboard: Array<{
    playerId: string;
    playerName: string;
    score: number;
  }>;
  rankedPlayers?: Array<{
    playerId: string;
    playerName: string;
    rank: number;
  }>;
  isSpeedChallenge?: boolean;
}

interface SpeedChallengeHostDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{
      id: string;
      name: string;
      score: number;
      avatar?: string;
    }>;
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: SpeedChallengeQuestion;
    questionStartTime?: number;
  };
  socket: WebSocketAdapter;
  onBackToGameSelection?: () => void;
  myPlayerId?: string;
  hostIsPlaying?: boolean;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
}

const SpeedChallengeHostDisplay: React.FC<SpeedChallengeHostDisplayProps> = ({ 
  roomState, 
  socket, 
  onBackToGameSelection,
  hostIsPlaying = false,
  onGoHome,
  onLoadingTimeout,
  myPlayerId,
}) => {
  const { t } = useTranslation();
  const [currentQuestion, setCurrentQuestion] = useState<SpeedChallengeQuestion | null>(
    roomState.currentQuestion || null
  );
  const [timeRemaining, setTimeRemaining] = useState<number>(
    roomState.currentQuestion?.timeLimit || 0
  );
  const [questionResult, setQuestionResult] = useState<QuestionResult | null>(null);
  const [showHostDidYouKnow, setShowHostDidYouKnow] = useState(false);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [waitingForNewGame, setWaitingForNewGame] = useState(false);
  const [liveAnswers, setLiveAnswers] = useState<LiveAnswer[]>([]);
  const [shouldUpdateStandings, setShouldUpdateStandings] = useState(false);
  
  // Host playing mode state
  const [answer, setAnswer] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [answerCorrect, setAnswerCorrect] = useState<boolean | null>(null);
  const [pointsEarned, setPointsEarned] = useState(0);
  const [rankPhrase, setRankPhrase] = useState<{ emoji: string; text: string; color: string } | null>(null);
  const [correctAnswerDisplay, setCorrectAnswerDisplay] = useState<string | null>(null);
  const [acceptedAnswers, setAcceptedAnswers] = useState<string[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isSubmittingRef = useRef<boolean>(false);
  
  const previousQuestionIdRef = useRef<string | undefined>(undefined);
  const roomNameRef = useRef<string>(roomState.roomName);
  // Use ref for players to avoid socket listener cleanup/setup race condition
  const playersRef = useRef(roomState.players);

  const gameEndData = useGameEndScreenData(roomState);
  
  // Helper to reset host playing state
  const resetHostPlayingState = () => {
    setAnswer('');
    setAnswerSubmitted(false);
    setAnswerCorrect(null);
    setPointsEarned(0);
    setRankPhrase(null);
    setCorrectAnswerDisplay(null);
    setAcceptedAnswers(null);
    setLiveAnswers([]);
    isSubmittingRef.current = false;
    // Focus input for quick typing
    if (hostIsPlaying) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  useEffect(() => {
    roomNameRef.current = roomState.roomName;
  }, [roomState.roomName]);

  // Keep playersRef in sync with roomState.players
  useEffect(() => {
    playersRef.current = roomState.players;
  }, [roomState.players]);

  // When server says game is finished (e.g. reconnection after missing game-ended), show game end screen
  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCurrentQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    }
  }, [roomState.gameState, gameEndData]);

  // Update local state when roomState.currentQuestion changes
  useEffect(() => {
    if (roomState.gameState === 'finished') return;
    if (roomState.currentQuestion) {
      const newQuestionId = roomState.currentQuestion.questionId || roomState.currentQuestion.id;
      const isNewQuestion = newQuestionId !== previousQuestionIdRef.current;
      
      // Use full timeLimit - user doesn't care about timer accuracy, just that game works
      const calculatedTimeRemaining = roomState.currentQuestion.timeLimit;
      
      // Update if it's a different question OR if we don't have a current question yet
      if (isNewQuestion || !currentQuestion) {
        if (isNewQuestion) {
          console.log(`SpeedChallengeHostDisplay: New question from room-state. ID: ${newQuestionId}`);
        } else {
          console.log(`SpeedChallengeHostDisplay: Refreshing question state (reconnection). ID: ${newQuestionId}, TimeRemaining: ${calculatedTimeRemaining}ms`);
        }
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(roomState.currentQuestion);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionResult(null);
        setShowHostDidYouKnow(false);
        // Reset standings update flag for new question
        setShouldUpdateStandings(false);
        // Only reset host playing state if it's a new question, not if we're just syncing mid-game or reconnecting
        if (isNewQuestion) {
          resetHostPlayingState();
        }
      }
    } else if (roomState.gameState === 'waiting') {
      setCurrentQuestion(null);
      setQuestionResult(null);
      setShowHostDidYouKnow(false);
      setWaitingForNewGame(false);
      setGameEnded(false);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomState.currentQuestion, roomState.questionStartTime, roomState.gameState, hostIsPlaying, currentQuestion, timeRemaining]);

  useEffect(() => {
    const handleQuestion = (data: SpeedChallengeQuestion) => {
      console.log('SpeedChallengeHostDisplay received question:', data);
      const newQuestionId = data.questionId || data.id;
      
      // Use full timeLimit on reconnection - user doesn't care about timer accuracy, just that game works
      // This simplifies the logic and ensures the game continues to flow
      const calculatedTimeRemaining = data.timeLimit;
      
      // ALWAYS update when question event is received (backend is sending a fresh question)
      // This ensures host receives new questions after reconnection
      // The question event is authoritative - it means backend is actively sending this question
      if (newQuestionId !== previousQuestionIdRef.current || !currentQuestion) {
        console.log(`SpeedChallengeHostDisplay: Updating from question event. ID: ${newQuestionId}, Previous: ${previousQuestionIdRef.current}`);
        previousQuestionIdRef.current = newQuestionId;
        setCurrentQuestion(data);
        setQuestionResult(null);
        setTimeRemaining(calculatedTimeRemaining);
        resetHostPlayingState();
      } else {
        // Even if same ID, update if we're receiving a question event (means backend is resending/refreshing)
        console.log(`SpeedChallengeHostDisplay: Refreshing question from event (same ID: ${newQuestionId})`);
        setCurrentQuestion(data);
        setTimeRemaining(calculatedTimeRemaining);
        setQuestionResult(null);
        resetHostPlayingState();
      }
    };

    const handleQuestionResult = (data: QuestionResult) => {
      console.log('Question result:', data);
      setQuestionResult(data);
      setShowHostDidYouKnow(false);
      // Trigger score update in StandingsSidebar
      setShouldUpdateStandings(true);
      // Reset after a short delay to allow the update to process
      setTimeout(() => {
        setShouldUpdateStandings(false);
      }, 100);
      // Trigger flip after delay
      setTimeout(() => {
        setShowHostDidYouKnow(true);
      }, 1500);
    };

    const handleGameEnded = (data: {
      leaderboard: Array<{ playerId: string; playerName: string; score: number }>;
      statistics?: GameStatistics;
    }) => {
      setGameEnded(true);
      const sortedLeaderboard = [...(data.leaderboard || [])].sort((a, b) => b.score - a.score);
      setFinalLeaderboard(sortedLeaderboard);
      setGameStatistics(data.statistics || null);
      setCurrentQuestion(null);
      setQuestionResult(null);
    };

    const handleGameStarted = () => {
      console.log('SpeedChallengeHostDisplay: game-started');
      setWaitingForNewGame(false);
      setGameEnded(false);
      setGameStatistics(null);
      setFinalLeaderboard([]);
      // Don't set currentQuestion to null here - let the question event set the actual question
      // This avoids a race condition where the UI gets stuck on "Loading question..."
      setQuestionResult(null);
      previousQuestionIdRef.current = undefined;
      resetHostPlayingState();
      socket.emit('get-room-state', { roomName: roomNameRef.current });
    };
    
    // Handle answer-received for host playing mode
    const handleAnswerReceived = (data: { 
      isCorrect: boolean; 
      points: number; 
      responseTime: number; 
      rank?: number | null;
      correctAnswer?: string;
      acceptedAnswers?: string[];
    }) => {
      console.log('SpeedChallengeHostDisplay answer-received:', data);
      isSubmittingRef.current = false;
      setAnswerSubmitted(true);
      setAnswerCorrect(data.isCorrect);
      setPointsEarned(data.points);
      if (data.rank) {
        // Generate rank phrase
        const pickRandom = (arr: string[]): string => arr[Math.floor(Math.random() * arr.length)];
        const rankColors: Record<number, string> = {
          1: '#FACC15', 2: '#CBD5E1', 3: '#F59E0B', 4: '#34D399', 5: '#34D399',
        };
        const rankEmojis: Record<number, string> = {
          1: '🥇', 2: '🥈', 3: '🥉', 4: '⚡', 5: '👍',
        };
        const rankKey = data.rank >= 1 && data.rank <= 5 ? String(data.rank) : 'default';
        const texts = t(`gameplay.speedChallenge.rankFeedback.${rankKey}`, { returnObjects: true }) as string[];
        const emoji = rankEmojis[data.rank] ?? '✓';
        const color = rankColors[data.rank] ?? '#34D399';
        setRankPhrase({ emoji, text: pickRandom(texts), color });
      }
      // Store correct answer info for wrong answers
      if (!data.isCorrect && data.correctAnswer) {
        setCorrectAnswerDisplay(data.correctAnswer);
        setAcceptedAnswers(data.acceptedAnswers || null);
      }
    };

    // Handle live answers from players
    const handlePlayerAnswered = (data: LiveAnswer) => {
      setLiveAnswers((prev) => {
        // Don't add duplicate answers from the same player
        if (prev.some((a) => a.playerId === data.playerId)) return prev;
        return [...prev, data];
      });
    };

    socket.on('question', handleQuestion);
    socket.on('question-result', handleQuestionResult);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);
    socket.on('answer-received', handleAnswerReceived);
    socket.on('player-answered', handlePlayerAnswered);

    return () => {
      // Pass specific handler to only remove THIS component's listener, not App.tsx's
      socket.off('question', handleQuestion);
      socket.off('question-result', handleQuestionResult);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
      socket.off('answer-received', handleAnswerReceived);
      socket.off('player-answered', handlePlayerAnswered);
    };
    // Using playersRef instead of roomState.players to avoid socket listener race condition
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, hostIsPlaying]);

  // Track previous players for reference
  const previousPlayersRef = useRef<typeof roomState.players>([]);
  useEffect(() => {
    previousPlayersRef.current = roomState.players;
  }, [roomState.players]);

  // Timer countdown
  useEffect(() => {
    if (currentQuestion && timeRemaining > 0 && !questionResult) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 100) return 0;
          return prev - 100;
        });
      }, 100);

      return () => clearInterval(timer);
    }
  }, [currentQuestion, timeRemaining, questionResult]);

  // Game ended screen
  if (gameEnded) {
    const handlePlayAnotherGame = () => {
      if (onBackToGameSelection) {
        onBackToGameSelection();
      } else {
        window.location.reload();
      }
    };

    const handlePlaySameGame = () => {
      if (socket && roomState) {
        setGameEnded(false);
        setFinalLeaderboard([]);
        setCurrentQuestion(null);
        setQuestionResult(null);
        setWaitingForNewGame(true);
        socket.emit('start-game', { roomName: roomState.roomName });
      }
    };

    return (
      <GameEndScreen
        finalLeaderboard={finalLeaderboard.length > 0 ? finalLeaderboard : (gameEndData?.finalLeaderboard ?? [])}
        gameStatistics={gameStatistics ?? gameEndData?.gameStatistics ?? null}
        onPlaySameGame={handlePlaySameGame}
        onPlayAnotherGame={handlePlayAnotherGame}
        onBackToGameSelection={onBackToGameSelection}
        roomName={roomState.roomName}
        gameType={roomState.gameType}
      />
    );
  }

  // Loading states
  if (!currentQuestion && !waitingForNewGame && !gameEnded) {
    return (
      <LoadingQuestion
        socket={socket}
        roomName={roomState.roomName}
        title={t('gameplay.speedChallenge.title')}
        subtitle={t('gameplay.loading.question')}
        onGoHome={onGoHome}
        onLongWaitTimeout={onLoadingTimeout}
      />
    );
  }

  if (waitingForNewGame) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{t('gameplay.speedChallenge.starting')}</h1>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <div style={{
              display: 'inline-block',
              width: '40px',
              height: '40px',
              border: '4px solid rgba(248, 250, 252, 0.3)',
              borderTop: '4px solid #F8FAFC',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="app">
        <div className="container">
          <h1 className="title">{t('common.loading')}</h1>
        </div>
      </div>
    );
  }

  const timeSeconds = Math.ceil(timeRemaining / 1000);
  const progress = (timeRemaining / currentQuestion.timeLimit) * 100;

  const handleRestartGame = () => {
    if (socket && roomState) {
      setWaitingForNewGame(true);
      setGameEnded(false);
      setCurrentQuestion(null);
      resetHostPlayingState();
      socket.emit('start-game', { roomName: roomState.roomName });
    }
  };

  const handleBackToGameSelection = () => {
    // Return everyone to game selection screen within the same room
    if (onBackToGameSelection) {
      onBackToGameSelection();
    }
  };
  
  // Handle answer submission when host is playing
  const handleSubmitAnswer = () => {
    if (!answer.trim() || !currentQuestion || answerSubmitted || isSubmittingRef.current) {
      return;
    }

    const questionId = currentQuestion.questionId || currentQuestion.id;
    if (!questionId) {
      console.error('Question ID not found');
      return;
    }

    isSubmittingRef.current = true;
    setAnswerSubmitted(true);

    socket.emit('submit-answer', {
      roomName: roomState.roomName,
      questionId: questionId,
      answer: answer.trim().toUpperCase(),
      timestamp: Date.now(),
    });
  };
  
  // Handle Enter key for answer submission
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };
  
  // Get challenge type hint - uses the actual question for pattern/shape types
  const getChallengeHint = (type: string, question?: string): string => {
    // For pattern and shape types, use the actual question as it's more specific
    if ((type === 'pattern' || type === 'shape') && question) {
      return question;
    }
    const hints: Record<string, string> = {
      'type-word': t('gameplay.speedChallenge.hints.typeWord'),
      'emoji-identify': t('gameplay.speedChallenge.hints.emojiIdentify'),
      'math': t('gameplay.speedChallenge.hints.math'),
      'reverse': t('gameplay.speedChallenge.hints.reverse'),
      'color-sequence': t('gameplay.speedChallenge.hints.colorSequence'),
      'pattern': t('gameplay.speedChallenge.hints.pattern'),
      'shape': t('gameplay.speedChallenge.hints.shape'),
    };
    return hints[type] || t('gameplay.speedChallenge.hints.default');
  };

  const getPromptLabel = (question: SpeedChallengeQuestion): string => {
    if (question.challengeType === 'type-word' || question.challengeType === 'reverse') {
      return question.displayContent.toUpperCase();
    }
    if (question.challengeType === 'math') {
      return `${question.displayContent} = ?`;
    }
    return question.displayContent.toUpperCase();
  };


  // Get display style based on challenge type - responsive sizes (reduced for big screens)
  const getDisplayStyle = (type: string) => {
    switch (type) {
      case 'emoji-identify':
      case 'shape':
        return { fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: '1.2' };
      case 'type-word':
      case 'reverse':
        return { fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', fontFamily: 'monospace', letterSpacing: '0.15em' };
      case 'math':
      case 'pattern':
        return { fontSize: 'clamp(1.2rem, 3vw, 1.8rem)', fontFamily: 'monospace' };
      case 'color-sequence':
        return { fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', letterSpacing: '0.3em' };
      default:
        return { fontSize: 'clamp(1.2rem, 3vw, 1.8rem)' };
    }
  };

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.back,
          onClick: handleBackToGameSelection,
          tooltip: t('gameplay.backToGameSelection')
        }}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: handleRestartGame,
          tooltip: t('gameplay.restartGame')
        }}
        centerContent={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1.5rem' }}>
            <span className="header-game-name" style={{ fontSize: '1.1rem', fontWeight: 'bold', color: '#F8FAFC' }}>
              {t('gameplay.speedChallenge.title')}
            </span>
            <div style={{ 
              fontSize: '1.2rem', 
              fontWeight: 'bold', 
              color: '#7C3AED',
              padding: '0.35rem 0.85rem',
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              borderRadius: '12px',
              border: '2px solid rgba(168, 85, 247, 0.65)',
              boxShadow: '0 6px 18px rgba(124, 58, 237, 0.18)',
            }}>
              {roomState.roomName}
            </div>
          </div>
        }
      />

      {/* Main game layout - desktop: left (question) | right (standings), mobile: stacked */}
      <div style={{
        minHeight: 'calc(100vh - 82px)',
        width: '100%',
        padding: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      }}>
        <DesktopLayout
          leftContent={
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
        <div
          className="container"
          style={{
            position: 'relative',
            overflow: 'hidden',
            padding: '1.5rem',
            marginTop: '0.5rem',
            marginBottom: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            borderRadius: '32px',
            background: 'radial-gradient(circle at top left, rgba(236, 72, 153, 0.34), transparent 24%), linear-gradient(135deg, #22053f 0%, #37106a 30%, #22053f 62%, #120022 100%)',
            boxShadow: '0 28px 80px rgba(10, 4, 28, 0.55)',
            minHeight: 'calc(100vh - 170px)',
          }}
        >
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(180deg, rgba(255,255,255,0.04), rgba(255,255,255,0) 26%)',
            pointerEvents: 'none',
          }} />
          <div style={{
            position: 'absolute',
            left: '-5%',
            bottom: '-8%',
            width: '58%',
            height: '46%',
            background: 'radial-gradient(circle, rgba(244, 114, 182, 0.16), transparent 62%)',
            pointerEvents: 'none',
          }} />

          <div className="timer-section" style={{ width: '100%', marginBottom: '1.8rem', position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '0.85rem',
              flexWrap: 'wrap',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.7rem',
                color: 'rgba(255,255,255,0.92)',
                fontWeight: 700,
                fontSize: '1.05rem',
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '2rem',
                  height: '2rem',
                  borderRadius: '999px',
                  background: 'rgba(255,255,255,0.1)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)',
                }}>
                  ⚡
                </span>
                Question {currentQuestion.questionNumber} of {currentQuestion.totalQuestions}
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.85rem',
                color: timeSeconds <= 5 ? '#FCA5A5' : '#FDBA74',
                fontWeight: 800,
                fontSize: 'clamp(2rem, 4vw, 3.5rem)',
                textShadow: '0 6px 18px rgba(251, 146, 60, 0.22)',
              }}>
                <span style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '3.4rem',
                  height: '3.4rem',
                  borderRadius: '999px',
                  border: `3px dashed ${timeSeconds <= 5 ? 'rgba(239, 68, 68, 0.65)' : 'rgba(251, 146, 60, 0.55)'}`,
                  background: 'rgba(255,255,255,0.06)',
                  fontSize: '1.45rem',
                }}>
                  ⏱
                </span>
                {timeSeconds}s
              </div>
            </div>
            <div style={{
              width: '100%',
              height: '14px',
              borderRadius: '999px',
              background: 'rgba(255,255,255,0.18)',
              overflow: 'hidden',
              boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.28)',
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                borderRadius: '999px',
                background: timeSeconds <= 5
                  ? 'linear-gradient(90deg, #FB7185, #F97316)'
                  : 'linear-gradient(90deg, #FDBA74, #EC4899 68%)',
                transition: 'width 0.1s linear',
              }} />
            </div>
          </div>

          <div className="game-display-centered-content" style={{ flex: 1, justifyContent: 'center', position: 'relative', zIndex: 1 }}>
          <h1 className="game-question-text" style={{
            color: '#FFFFFF',
            marginBottom: '2rem',
            letterSpacing: '-0.03em',
            maxWidth: '13ch',
            fontSize: 'clamp(2.3rem, 4.8vw, 5rem)',
            lineHeight: 1.08,
            textShadow: '0 10px 30px rgba(0, 0, 0, 0.32)',
          }}>
            {currentQuestion.question}
          </h1>

          <div style={{
            width: '100%',
            maxWidth: hostIsPlaying ? '760px' : '700px',
            margin: '0 auto',
            padding: '1.5rem 1.6rem',
            borderRadius: '28px',
            background: 'linear-gradient(180deg, rgba(70, 15, 122, 0.84), rgba(36, 7, 74, 0.96))',
            border: '2px solid rgba(244, 114, 182, 0.7)',
            boxShadow: '0 0 0 2px rgba(255,255,255,0.05) inset, 0 12px 38px rgba(219, 39, 119, 0.2)',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
            }}>
              <div style={{
                fontSize: 'clamp(1.8rem, 3vw, 2.7rem)',
                lineHeight: 1,
              }}>
                ⚡
              </div>
              <div style={{
                width: '1px',
                alignSelf: 'stretch',
                background: 'rgba(255,255,255,0.14)',
              }} />
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{
                  ...getDisplayStyle(currentQuestion.challengeType),
                  color: '#F8FAFC',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  textAlign: 'left',
                }}>
                  {getPromptLabel(currentQuestion)}
                </div>
                {!hostIsPlaying && (
                  <div style={{
                    marginTop: '0.55rem',
                    color: 'rgba(255,255,255,0.58)',
                    fontSize: '1rem',
                    textAlign: 'left',
                  }}>
                    Players type their answer on their phones...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Show correct answer with flip to Did you know */}
          {questionResult && (() => {
            const didYouKnowFlipped = !!(showHostDidYouKnow && currentQuestion.interesting);
            return (
            <div style={{
              perspective: '1000px',
              marginTop: '2rem',
              marginBottom: currentQuestion.interesting ? '2rem' : undefined,
              maxWidth: '700px',
              width: '100%',
              minHeight: currentQuestion.interesting ? '220px' : undefined,
            }}>
              <div style={{
                position: 'relative',
                width: '100%',
                transformStyle: 'preserve-3d',
                transition: 'transform 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                transform: didYouKnowFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              }}>
                {/* Front - Correct Answer */}
                <div style={{
                  position: didYouKnowFlipped ? 'absolute' : 'relative',
                  top: didYouKnowFlipped ? 0 : undefined,
                  left: didYouKnowFlipped ? 0 : undefined,
                  right: didYouKnowFlipped ? 0 : undefined,
                  width: '100%',
                  padding: '1.5rem 2rem',
                  backgroundColor: 'rgba(16, 185, 129, 0.18)',
                  border: '2px solid rgba(52, 211, 153, 0.8)',
                  borderRadius: '22px',
                  textAlign: 'center',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  boxSizing: 'border-box',
                }}>
                  <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.7)', margin: 0 }}>
                    {t('gameplay.speedChallenge.correctAnswerColon')}
                  </p>
                  <p style={{ fontSize: '2rem', color: '#34D399', fontWeight: 'bold', margin: '0.5rem 0 0 0' }}>
                    {questionResult.correctAnswer}
                  </p>
                </div>
                
                {/* Back - Did you know? */}
                {currentQuestion.interesting && (
                  <div style={{
                    position: didYouKnowFlipped ? 'relative' : 'absolute',
                    top: didYouKnowFlipped ? undefined : 0,
                    left: didYouKnowFlipped ? undefined : 0,
                    right: didYouKnowFlipped ? undefined : 0,
                    width: '100%',
                    padding: '1.5rem 2rem',
                    paddingBottom: '3rem',
                    backgroundColor: 'rgba(16, 185, 129, 0.18)',
                    border: '2px solid rgba(52, 211, 153, 0.8)',
                    borderRadius: '22px',
                    textAlign: 'center',
                    backfaceVisibility: 'hidden',
                    WebkitBackfaceVisibility: 'hidden',
                    transform: 'rotateY(180deg)',
                    overflow: 'visible',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    boxSizing: 'border-box',
                  }}>
                    <p style={{ fontSize: '1.25rem', color: '#34D399', fontWeight: 'bold', margin: '0 0 0.8rem 0' }}>
                      💡 Did you know?
                    </p>
                    <p style={{ fontSize: '1.15rem', color: '#fff', lineHeight: '1.6', opacity: 0.9, margin: 0 }}>
                      {currentQuestion.interesting}
                    </p>
                  </div>
                )}
              </div>
            </div>
            );
          })()}
          
          {/* Host Playing Mode - Answer Input */}
          {hostIsPlaying && !answerSubmitted && (
            <div style={{ 
              marginTop: '2rem', 
              width: '100%',
              maxWidth: '700px',
              margin: '2rem auto 0',
            }}>
              <style>{`
                .speed-input-host::placeholder {
                  font-size: 0.9rem;
                  letter-spacing: 0;
                  text-transform: none;
                }
              `}</style>
              <input
                ref={inputRef}
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                placeholder={getChallengeHint(currentQuestion.challengeType, currentQuestion.question)}
                autoComplete="off"
                autoCapitalize="characters"
                className="speed-input-host"
                style={{
                  width: '100%',
                  padding: '1.2rem',
                  fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
                  fontFamily: 'monospace',
                  textAlign: 'left',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  border: '2px solid rgba(244, 114, 182, 0.45)',
                  borderRadius: '20px',
                  color: '#fff',
                  outline: 'none',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.04)',
                }}
              />
              <button
                onClick={handleSubmitAnswer}
                disabled={!answer.trim()}
                className="button"
                style={{
                  width: '100%',
                  marginTop: '1rem',
                  background: answer.trim() ? 'linear-gradient(90deg, #EC4899, #A855F7)' : 'rgba(248, 250, 252, 0.3)',
                  opacity: answer.trim() ? 1 : 0.5,
                  cursor: answer.trim() ? 'pointer' : 'not-allowed',
                  borderRadius: '16px',
                }}
              >
                {t('gameplay.submitAnswer')}
              </button>
            </div>
          )}
          
          {/* Host Playing Mode - Answer Submitted Feedback */}
          {hostIsPlaying && answerSubmitted && (
            <div style={{ 
              marginTop: '2rem', 
              width: '100%',
              maxWidth: '700px',
              margin: '2rem auto 0',
            }}>
              <div style={{
                padding: '1.5rem',
                backgroundColor: answerCorrect ? 'rgba(52, 211, 153, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                border: `2px solid ${answerCorrect ? '#34D399' : '#EF4444'}`,
                borderRadius: '15px',
                textAlign: 'center',
                color: '#fff',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                }}>
                  <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>
                    {answerCorrect ? '✓' : '✗'}
                  </span>
                  <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                    {answerCorrect 
                      ? (rankPhrase ? `${rankPhrase.text} (+${pointsEarned})` : t('gameplay.speedChallenge.correctShort')) 
                      : t('gameplay.feedback.wrongAnswer')}
                  </span>
                </div>
                
                {/* Show correct answer when wrong */}
                {!answerCorrect && correctAnswerDisplay && (
                  <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(239, 68, 68, 0.3)' }}>
                    <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                      {t('gameplay.speedChallenge.correctAnswerLower')}
                    </p>
                    <p style={{ fontSize: '1.2rem', color: '#fff', fontWeight: 'bold', margin: '0.3rem 0' }}>
                      {correctAnswerDisplay}
                    </p>
                    {acceptedAnswers && acceptedAnswers.length > 1 && (
                      <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', margin: '0.5rem 0 0 0' }}>
                        Also accepted: {acceptedAnswers.filter(a => a !== correctAnswerDisplay).join(', ')}
                      </p>
                    )}
                  </div>
                )}
                
                <div style={{ marginTop: '1rem' }}>
                  <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.6)', margin: 0 }}>
                    Your answer: <span style={{ color: '#fff', fontWeight: 'bold' }}>{answer}</span>
                  </p>
                </div>
              </div>
              
              <WaitingSpinner isLoading={false} />
            </div>
          )}
          </div>
        </div>
            </div>
          }
          rightContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={liveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={shouldUpdateStandings}
              variant="light-card"
              theme={{
                primary: '#A855F7',
                primaryLight: 'rgba(168, 85, 247, 0.12)',
                accent: '#A855F7',
              }}
            />
          }
          mobileOnlyContent={
            <StandingsSidebar
              players={roomState.players}
              liveAnswers={liveAnswers}
              totalPlayers={roomState.players.length}
              questionId={currentQuestion?.questionId || currentQuestion?.id}
              shouldUpdateScores={shouldUpdateStandings}
              isMobile={true}
              variant="light-card"
              theme={{
                primary: '#A855F7',
                primaryLight: 'rgba(168, 85, 247, 0.12)',
                accent: '#A855F7',
              }}
            />
          }
          rightWidth="320px"
        />
      </div>

    </div>
  );
};

export default SpeedChallengeHostDisplay;

